import { __setAppInfo, type BootstrapOptions as MinimalBootstrapOptions, type PackageJson } from '@innolab-trader/bootstrap'
export type { PackageJson } from '@innolab-trader/bootstrap'
import { ConfigManager, defaultConfig, type AppConfig } from '@innolab-trader/config'
import {
  ErrorBoundaryReporter,
  ConsoleErrorReporter,
  BugsnagProvider,
  setupGlobalErrorHandlers,
  type BugsnagConfig,
  type ErrorReporter,
} from '@innolab-trader/error-handling'
import {
  FeatureFlags,
  LocalStorageFeatureFlagsProvider,
  LaunchDarklyProvider,
  type LaunchDarklyConfig,
} from '@innolab-trader/feature-flags'

export interface BootstrapOptions extends MinimalBootstrapOptions {
  analytics?: {
    enabled?: boolean
    heap?: { appId: string; options?: Record<string, unknown> }
    useHeap?: boolean
    sampleRate?: number
  }
  errorReporting?: {
    reporter?: ErrorReporter
    setupGlobalHandlers?: boolean
    bugsnag?: BugsnagConfig
    useBugsnag?: boolean
  }
  featureFlags?: {
    defaultFlags?: Record<string, boolean>
    launchDarkly?: LaunchDarklyConfig
    useLaunchDarkly?: boolean
  }
  webVitals?: {
    enabled?: boolean
    reportToConsole?: boolean
    reportToHeap?: boolean
    reportToAnalytics?: boolean
    debug?: boolean
  }
  config?: Partial<AppConfig>
}

export function initializeBootstrap(options: BootstrapOptions = {}, packageJson?: PackageJson): void {
  const appName = packageJson?.name || 'Unknown App'
  const appVersion = packageJson?.version || options.config?.version || '0.0.0'

  __setAppInfo({ name: appName, version: appVersion, mobileApps: options.mobileApps })
   
  console.log(`[Shell Bootstrap] Initializing ${appName} v${appVersion}`)

  ConfigManager.initialize({ ...defaultConfig, ...options.config })

  // Error reporting
  let reporter: ErrorReporter
  if (options.errorReporting?.useBugsnag && options.errorReporting?.bugsnag) {
    reporter = new BugsnagProvider({ ...options.errorReporting.bugsnag, appVersion, metadata: { appName, appVersion } })
     
    console.log(`[Shell Bootstrap] Bugsnag enabled for ${appName} v${appVersion}`)
  } else if (options.errorReporting?.reporter) {
    reporter = options.errorReporting.reporter
  } else {
    reporter = new ConsoleErrorReporter()
     
    console.log('[Shell Bootstrap] Console error reporting enabled')
  }
  if (options.errorReporting?.setupGlobalHandlers !== false) setupGlobalErrorHandlers(reporter)
  ErrorBoundaryReporter.setReporter(reporter)

  // Feature flags
  if (options.featureFlags?.useLaunchDarkly && options.featureFlags.launchDarkly) {
    const fallback = new LocalStorageFeatureFlagsProvider(options.featureFlags.defaultFlags || {})
    const ldProvider = new LaunchDarklyProvider(options.featureFlags.launchDarkly, fallback)
    FeatureFlags.setProvider(ldProvider)
     
    console.log(`[Shell Bootstrap] LaunchDarkly enabled for ${appName} v${appVersion}`)
  } else if (options.featureFlags?.defaultFlags) {
    FeatureFlags.setProvider(new LocalStorageFeatureFlagsProvider(options.featureFlags.defaultFlags))
     
    console.log('[Shell Bootstrap] LocalStorage feature flags enabled')
  }

   
  console.log('[Shell Bootstrap] Initialized with config:', ConfigManager.getAll())
}
